import { useEffect, useState } from 'react'
import { FormikErrors, useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import { getHashFile } from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/types'
import {
  EscritoFormInitialValues,
  EscritosFormValues
} from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { LEXNET_MAX_MAIN_FILE_SIZE, LEXNET_MAX_TOTAL_FILE_SIZE } from 'src/constants/lexnet'
import { useAlertContext } from 'src/contexts/AlertContext'
import { FormField } from 'src/enums/Form'
import { DocumentoAnexoType, FileHashType } from 'src/interfaces/EscritosLegales'

type Props = {
  id: string
  multiple: boolean
}

type ResponseProps = {
  anexosFiles: File[]
  setAnexosFiles: (prevState: File[]) => void
  setInputFile: (file: File) => void
  closeWarning: () => void
  validateMimeType: (value: File) => void
  validateFileNameNotInData: (value: File, hash: string | null) => boolean
  validateFileSize: (value: File) => boolean
  errors: FormikErrors<EscritosFormValues>
}

export default function useInputFile(props: Props): ResponseProps {
  const { id, multiple } = props
  const { values, errors, setFieldValue, setFieldError } = useFormikContext<EscritosFormValues>()
  const [fieldInfo] = useField(id)
  const { addAlert } = useAlertContext()
  const { t } = useTranslation()
  const FileNameNotInData = t('form.validation.filename_not_in_data')
  const FileMimeError = t('form.validation.filename_mime_error')
  const FileSameHash = t('form.validation.filename_same_hash')

  const [anexosFiles, setAnexosFiles] = useState<File[]>([])

  function closeWarning() {
    if (!multiple) {
      setFieldValue(id, EscritoFormInitialValues[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO])
    } else {
      anexosFiles.pop()
      const newAnexo = [...anexosFiles]
      setFieldValue(id, newAnexo)
    }
    setFieldError(id, '')
  }

  const validateFileNameNotInData = (value: File, hash: string | null) => {
    let message: string | null = null
    if (values[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO]?.file?.name === value.name) {
      message = FileNameNotInData
    }
    if (
      values[FormField.DOCUMENTOS_ANEXOS_ARCHIVO].some(
        (doc: FileHashType) => doc.file.name === value.name
      )
    ) {
      message = FileNameNotInData
    }
    if (values[FormField.DOCUMENTO_PRINCIPAL][0]?.hash === hash) {
      message = FileSameHash
    }
    if (values[FormField.DOCUMENTOS_ANEXOS].some((f: DocumentoAnexoType) => f.hash === hash)) {
      message = FileSameHash
    }
    if (message) {
      addAlert({
        message,
        type: AlertType.ERROR
      })
      return false
    }
    return true
  }

  const validateFileSize = (value: File) => {
    let totalSize = value?.size || 0
    if (values[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO]) {
      const aux = values[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO]?.file?.size
      if (aux && multiple) {
        totalSize += aux
      }
    }
    if (values[FormField.DOCUMENTOS_ANEXOS_ARCHIVO]) {
      values[FormField.DOCUMENTOS_ANEXOS_ARCHIVO].forEach((v: FileHashType) => {
        totalSize += v.file.size
      })
    }
    const mainFileExceeds = !multiple && value.size > LEXNET_MAX_MAIN_FILE_SIZE
    const totalExceeds = totalSize > LEXNET_MAX_TOTAL_FILE_SIZE
    setFieldValue(FormField.EXCESO_CABIDA, mainFileExceeds || totalExceeds)
    return true
  }

  const validateMimeType = (value: File) => {
    const fileReader = new FileReader()
    fileReader.onloadend = function(e) {
      if (e.target && e.target.result) {
        const arr = new Uint8Array(e.target.result as any).subarray(0, 4)
        let header = ''
        for (let i = 0; i < arr.length; i += 1) {
          header += arr[i].toString(16)
        }

        if (header !== '25504446') {
          // PDF Mime header
          addAlert({
            message: FileMimeError,
            type: AlertType.ERROR,
            closeAction: closeWarning
          })
        }
      }
    }

    fileReader.readAsArrayBuffer(value)
  }

  async function setInputFile(file: File) {
    const hash: string | null = await getHashFile(file)
    if (validateFileNameNotInData(file, hash) && validateFileSize(file)) {
      if (!multiple) {
        setFieldValue(id, { file, hash })
        validateMimeType(file)
      } else {
        const newAnexo = [...anexosFiles, { file, hash }]
        setFieldValue(id, newAnexo)
      }
    }
  }

  useEffect(() => {
    if (multiple) {
      setAnexosFiles(fieldInfo.value)
      validateFileSize(new File([''], 'emptyFile'))
    }
    // eslint-disable-next-line
  }, [fieldInfo.value, multiple])

  return {
    anexosFiles,
    setAnexosFiles,
    setInputFile,
    validateFileNameNotInData,
    validateFileSize,
    validateMimeType,
    closeWarning,
    errors
  }
}
